/**
 * Référence de l'API partenaire v1, source de la page Documentation et de sa
 * console d'essai. Miroir de `backend/src/routes/partner-api.routes.js` :
 * toute route ajoutée là doit être décrite ici, avec ses réponses possibles.
 */

export interface FieldSpec {
  name: string
  type: string
  required: boolean
  description: string
  /** Valeurs acceptées quand le champ est une énumération. */
  values?: { value: string; description: string }[]
  default?: string
}

export interface ResponseSpec {
  status: number
  code?: string
  label: string
  description: string
  example: string
}

export interface ApiEndpoint {
  id: string
  method: 'GET' | 'POST'
  path: string
  title: string
  description: string
  idempotent?: boolean
  pathParams?: FieldSpec[]
  queryParams?: FieldSpec[]
  bodyFields?: FieldSpec[]
  sampleBody?: string
  responses: ResponseSpec[]
}

// ── Erreurs communes à toutes les routes (chaîne d'authentification) ────────
const AUTH_ERRORS: ResponseSpec[] = [
  {
    status: 401,
    code: 'missing_api_key',
    label: 'Clé absente',
    description: "Aucune clé n'a été fournie dans Authorization ou x-api-key.",
    example: `{
  "error": {
    "code": "missing_api_key",
    "message": "Clé API manquante. Fournissez-la via l'en-tête Authorization: Bearer yk_… ou x-api-key."
  }
}`,
  },
  {
    status: 401,
    code: 'invalid_api_key',
    label: 'Clé inconnue ou révoquée',
    description: 'La clé ne correspond à aucune clé active.',
    example: `{
  "error": { "code": "invalid_api_key", "message": "Clé API inconnue ou révoquée." }
}`,
  },
  {
    status: 401,
    code: 'key_mode_not_supported',
    label: 'Mauvais environnement',
    description:
      "Une clé de test a été utilisée sur l'URL de production, ou l'inverse. Le champ docUrl rappelle la bonne base URL.",
    example: `{
  "error": {
    "code": "key_mode_not_supported",
    "message": "Les clés de test ne sont pas acceptées sur cet environnement. Utilisez la base URL sandbox.",
    "docUrl": "https://partners.youdego.app/docs"
  }
}`,
  },
  {
    status: 403,
    code: 'partner_suspended',
    label: 'Compte suspendu',
    description: 'Votre compte partenaire a été suspendu par Youdégo.',
    example: `{
  "error": { "code": "partner_suspended", "message": "Compte partenaire suspendu. Contactez le support Youdégo." }
}`,
  },
  {
    status: 429,
    code: 'rate_limit_exceeded',
    label: 'Trop de requêtes',
    description:
      '300 requêtes par minute et par clé, dont 30 créations. Les en-têtes RateLimit-* indiquent le quota restant.',
    example: `{
  "error": { "code": "rate_limit_exceeded", "message": "Trop de requêtes. Limite : 300 requêtes par minute par clé API." }
}`,
  },
  {
    status: 500,
    code: 'internal_error',
    label: 'Erreur interne',
    description: 'Incident côté Youdégo. La requête peut être rejouée.',
    example: `{
  "error": { "code": "internal_error", "message": "Erreur interne." }
}`,
  },
]

const IDEMPOTENCY_ERRORS: ResponseSpec[] = [
  {
    status: 400,
    code: 'missing_idempotency_key',
    label: 'Idempotency-Key absente',
    description: "L'en-tête Idempotency-Key est obligatoire sur cette route.",
    example: `{
  "error": {
    "code": "missing_idempotency_key",
    "message": "L'en-tête Idempotency-Key est requis sur cet endpoint (chaîne unique de moins de 256 caractères)."
  }
}`,
  },
  {
    status: 409,
    code: 'idempotency_key_reused',
    label: 'Clé réutilisée',
    description: 'Cette Idempotency-Key a déjà servi avec un corps différent.',
    example: `{
  "error": {
    "code": "idempotency_key_reused",
    "message": "Cette Idempotency-Key a déjà été utilisée avec un corps de requête différent."
  }
}`,
  },
  {
    status: 409,
    code: 'idempotency_key_in_flight',
    label: 'Requête déjà en cours',
    description: 'Une requête identique est en cours de traitement. Réessayez dans quelques secondes.',
    example: `{
  "error": {
    "code": "idempotency_key_in_flight",
    "message": "Une requête avec cette Idempotency-Key est déjà en cours de traitement. Réessayez dans quelques instants."
  }
}`,
  },
]

const NOT_FOUND = (what: string): ResponseSpec => ({
  status: 404,
  code: 'not_found',
  label: 'Introuvable',
  description: `${what} n'existe pas, ou n'appartient pas à votre compte.`,
  example: `{
  "error": { "code": "not_found", "message": "${what} introuvable." }
}`,
})

// ── Valeurs d'énumération réutilisées ───────────────────────────────────────
const DELIVERY_MODES = [
  {
    value: 'EXPRESS',
    description:
      'Course immédiate. La recherche de livreur est asynchrone : réponse 202 avec une demande, puis événement delivery.request.accepted.',
  },
  {
    value: 'STANDARD',
    description:
      'Course groupée dans la journée, moins chère. Créée immédiatement (201). Uniquement pendant la fenêtre de commande.',
  },
]

const DELIVERY_TYPES = [
  { value: 'COLIS', description: 'Colis générique (valeur par défaut).' },
  { value: 'REPAS', description: 'Repas ou plat préparé.' },
  { value: 'COURSES', description: 'Achats, provisions.' },
  { value: 'DOCUMENTS', description: 'Documents, plis administratifs.' },
  { value: 'MEDICAMENTS', description: 'Produits pharmaceutiques.' },
  { value: 'FLEURS', description: 'Fleurs et végétaux.' },
  { value: 'AUTRE', description: 'Tout autre contenu.' },
]

const PAYMENT_METHODS = [
  { value: 'CASH', description: 'Espèces encaissées par le livreur à la remise (valeur par défaut).' },
  {
    value: 'FEDAPAY',
    description:
      'Mobile Money payé à l\'avance : créez le paiement via POST /v1/payments, attendez payment.succeeded, puis passez son id dans payment.paymentId.',
  },
]

const PAYMENT_PROVIDERS = [
  { value: 'MTN_MOMO', description: 'MTN Mobile Money (valeur par défaut).' },
  { value: 'MOOV_MONEY', description: 'Moov Money.' },
  { value: 'CELTIIS_MONEY', description: 'Celtiis Money.' },
]

const DELIVERY_STATUSES = [
  { value: 'created', description: "Créée, en attente d'un livreur (mode STANDARD)." },
  { value: 'assigned', description: 'Un livreur est assigné et se rend au ramassage.' },
  { value: 'picked_up', description: 'Colis récupéré, en cours d\'acheminement.' },
  { value: 'delivered', description: 'Colis remis au destinataire.' },
  { value: 'canceled', description: 'Course annulée.' },
  { value: 'incident', description: 'Incident déclaré par le livreur.' },
]

const SANDBOX_SCENARIOS_ENUM = [
  { value: 'request_failed', description: 'Aucun livreur trouvé → delivery.request.failed.' },
  { value: 'delivery_canceled', description: 'Annulation après assignation → delivery.status.canceled.' },
  { value: 'delivery_incident', description: 'Incident après ramassage → delivery.status.incident.' },
]

export const API_ENDPOINTS: ApiEndpoint[] = [
  {
    id: 'me',
    method: 'GET',
    path: '/me',
    title: 'Vérifier une clé',
    description:
      "Retourne le partenaire et le mode associés à la clé utilisée. Le premier appel à faire pour valider votre configuration.",
    responses: [
      {
        status: 200,
        label: 'Clé valide',
        description: 'La clé est active et rattachée à votre compte.',
        example: `{
  "partner": { "id": "02ec282d-…", "name": "Votre entreprise" },
  "mode": "TEST",
  "keyId": "45faa9c7-…",
  "keyName": "Serveur de test",
  "billingMode": "PER_DELIVERY"
}`,
      },
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'config',
    method: 'GET',
    path: '/config',
    title: 'Valeurs acceptées',
    description:
      "Toutes les énumérations de l'API (modes, types, moyens de paiement, statuts) et l'état de la fenêtre de commande STANDARD. Interrogez-la au lieu de figer ces listes dans votre code.",
    responses: [
      {
        status: 200,
        label: 'Configuration',
        description: 'Les valeurs acceptées au moment de l\'appel.',
        example: `{
  "object": "config",
  "mode": "TEST",
  "deliveryModes": [ { "value": "EXPRESS", "description": "…" }, { "value": "STANDARD", "description": "…" } ],
  "deliveryTypes": ["COLIS", "REPAS", "COURSES", "DOCUMENTS", "MEDICAMENTS", "FLEURS", "AUTRE"],
  "paymentMethods": [ { "value": "CASH", "…": "…" }, { "value": "FEDAPAY", "…": "…" } ],
  "paymentProviders": [ { "value": "MTN_MOMO", "…": "…" } ],
  "deliveryStatuses": [ … ],
  "deliveryRequestStatuses": [ … ],
  "paymentStatuses": [ … ],
  "currency": "XOF",
  "standardOrdering": { "open": false, "message": "Les commandes STANDARD sont closes pour aujourd'hui…" },
  "sandbox": { "scenarios": ["request_failed", "delivery_canceled", "delivery_incident"], "note": "…" }
}`,
      },
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'estimates',
    method: 'POST',
    path: '/estimates',
    title: 'Estimer un prix',
    description:
      "Calcule la distance et le tarif d'une course avant de la créer. N'engage rien et n'est pas facturé.",
    bodyFields: [
      { name: 'pickup.latitude', type: 'number', required: true, description: 'Latitude du point de ramassage (-90 à 90).' },
      { name: 'pickup.longitude', type: 'number', required: true, description: 'Longitude du point de ramassage (-180 à 180).' },
      { name: 'dropoff.latitude', type: 'number', required: true, description: 'Latitude du point de livraison.' },
      { name: 'dropoff.longitude', type: 'number', required: true, description: 'Longitude du point de livraison.' },
      {
        name: 'mode',
        type: 'string',
        required: false,
        default: 'EXPRESS',
        description: 'Mode de livraison à tarifer.',
        values: DELIVERY_MODES,
      },
    ],
    sampleBody: `{
  "pickup":  { "latitude": 6.3703, "longitude": 2.3912 },
  "dropoff": { "latitude": 6.3654, "longitude": 2.4183 },
  "mode": "EXPRESS"
}`,
    responses: [
      {
        status: 200,
        label: 'Estimation',
        description: 'Le montant est en francs CFA, entier.',
        example: `{
  "object": "estimate",
  "mode": "EXPRESS",
  "amount": 700,
  "currency": "XOF",
  "distanceKm": 4.42,
  "estimatedDurationMin": 11
}`,
      },
      {
        status: 400,
        code: 'invalid_request',
        label: 'Coordonnées invalides',
        description: 'Une latitude ou longitude est absente ou hors bornes.',
        example: `{
  "error": { "code": "invalid_request", "message": "pickup.latitude et pickup.longitude sont requis (nombres valides)" }
}`,
      },
      {
        status: 422,
        code: 'estimate_unavailable',
        label: 'Itinéraire introuvable',
        description: "Aucun trajet routier n'a pu être calculé entre ces deux points.",
        example: `{
  "error": { "code": "estimate_unavailable", "message": "Impossible de calculer un itinéraire entre ces deux points." }
}`,
      },
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'create-delivery',
    method: 'POST',
    path: '/deliveries',
    title: 'Créer une livraison',
    description:
      "Le code de réponse dépend du mode : 202 en EXPRESS (une demande est ouverte, la recherche de livreur se poursuit en arrière-plan) et 201 en STANDARD (la livraison existe immédiatement).",
    idempotent: true,
    bodyFields: [
      { name: 'mode', type: 'string', required: false, default: 'EXPRESS', description: 'Mode de livraison.', values: DELIVERY_MODES },
      { name: 'type', type: 'string', required: false, default: 'COLIS', description: 'Nature du contenu transporté.', values: DELIVERY_TYPES },
      { name: 'pickup.latitude', type: 'number', required: true, description: 'Latitude du point de ramassage.' },
      { name: 'pickup.longitude', type: 'number', required: true, description: 'Longitude du point de ramassage.' },
      { name: 'pickup.comment', type: 'string', required: false, description: 'Précision pour le livreur (étage, repère…).' },
      { name: 'dropoff.latitude', type: 'number', required: true, description: 'Latitude du point de livraison.' },
      { name: 'dropoff.longitude', type: 'number', required: true, description: 'Longitude du point de livraison.' },
      { name: 'dropoff.comment', type: 'string', required: false, description: 'Précision pour la remise.' },
      { name: 'recipient.name', type: 'string', required: true, description: 'Nom du destinataire.' },
      { name: 'recipient.phone', type: 'string', required: true, description: 'Téléphone du destinataire, format local (0XXXXXXXXX).' },
      { name: 'notes', type: 'string', required: false, description: 'Note libre transmise au livreur.' },
      {
        name: 'externalRef',
        type: 'string',
        required: false,
        description:
          'Votre propre référence (255 caractères max). Unique par partenaire : elle permet de retrouver la course via GET /v1/deliveries?externalRef=…',
      },
      { name: 'payment.method', type: 'string', required: false, default: 'CASH', description: 'Mode de règlement de la course.', values: PAYMENT_METHODS },
      {
        name: 'payment.paymentId',
        type: 'string',
        required: false,
        description: 'Identifiant du paiement déjà confirmé. Obligatoire si payment.method vaut FEDAPAY.',
      },
      {
        name: 'sandbox.scenario',
        type: 'string',
        required: false,
        description: 'Mode test uniquement : force un cas d\'échec au lieu du déroulé nominal.',
        values: SANDBOX_SCENARIOS_ENUM,
      },
    ],
    sampleBody: `{
  "mode": "EXPRESS",
  "type": "COLIS",
  "pickup":  { "latitude": 6.3703, "longitude": 2.3912, "comment": "Boutique, rue 12" },
  "dropoff": { "latitude": 6.3654, "longitude": 2.4183, "comment": "Portail bleu" },
  "recipient": { "name": "Awa Dossou", "phone": "0197000000" },
  "notes": "Colis fragile",
  "externalRef": "CMD-2041",
  "payment": { "method": "CASH" }
}`,
    responses: [
      {
        status: 202,
        label: 'EXPRESS — recherche lancée',
        description:
          "Une demande est ouverte. Suivez-la par webhook (delivery.request.*) ou en interrogeant GET /v1/delivery-requests/:id. Aucune livraison n'existe encore.",
        example: `{
  "object": "delivery_request",
  "id": "66584ffa-…",
  "status": "pending",
  "mode": "EXPRESS",
  "externalRef": "CMD-2041",
  "deliveryId": null,
  "pricing": { "amount": 700, "currency": "XOF", "distanceKm": 4.42 },
  "expiresAt": "2026-08-18T16:52:23.414Z",
  "estimatedWaitSeconds": 300,
  "instructions": { "polling": "/api/v1/delivery-requests/66584ffa-…", "webhooks": "…" }
}`,
      },
      {
        status: 201,
        label: 'STANDARD — livraison créée',
        description: 'La livraison existe et sera groupée avec d\'autres dans la journée.',
        example: `{
  "object": "delivery",
  "id": "1343228b-…",
  "status": "created",
  "mode": "STANDARD",
  "externalRef": "CMD-2041",
  "recipient": { "name": "Awa Dossou", "phone": "0197000000" },
  "courier": null,
  "pricing": { "amount": 700, "currency": "XOF", "distanceKm": 4.42 },
  "timestamps": { "createdAt": "…", "assignedAt": null, "pickedUpAt": null, "deliveredAt": null }
}`,
      },
      {
        status: 400,
        code: 'invalid_request',
        label: 'Champs manquants ou invalides',
        description: 'Coordonnées hors bornes, destinataire absent, externalRef trop long…',
        example: `{
  "error": { "code": "invalid_request", "message": "recipient.name et recipient.phone sont requis." }
}`,
      },
      {
        status: 400,
        code: 'standard_ordering_closed',
        label: 'Fenêtre STANDARD fermée',
        description:
          "Les courses STANDARD ne sont commandables que pendant la fenêtre du jour. Repassez en EXPRESS ou réessayez demain.",
        example: `{
  "error": {
    "code": "standard_ordering_closed",
    "message": "Les commandes STANDARD sont closes pour aujourd'hui (fenêtre 8h-16h). Passez en mode EXPRESS pour être livré maintenant."
  }
}`,
      },
      {
        status: 400,
        code: 'payment_id_required',
        label: 'Paiement non fourni',
        description: 'payment.method vaut FEDAPAY mais aucun payment.paymentId n\'accompagne la requête.',
        example: `{
  "error": {
    "code": "payment_id_required",
    "message": "payment.paymentId est requis pour le paiement FEDAPAY (créez d'abord le paiement via POST /v1/payments)."
  }
}`,
      },
      {
        status: 400,
        code: 'payment_not_confirmed',
        label: 'Paiement non confirmé',
        description: "Le paiement référencé n'est pas encore au statut success.",
        example: `{
  "error": { "code": "payment_not_confirmed", "message": "Le paiement n'est pas confirmé." }
}`,
      },
      {
        status: 400,
        code: 'payment_already_used',
        label: 'Paiement déjà consommé',
        description: 'Ce paiement est déjà rattaché à une autre livraison.',
        example: `{
  "error": { "code": "payment_already_used", "message": "Ce paiement est déjà lié à une livraison." }
}`,
      },
      {
        status: 400,
        code: 'billing_mode_not_supported',
        label: 'Mode de facturation indisponible',
        description:
          "Votre compte est configuré sur un mode de facturation que l'API n'expose pas encore (wallet prépayé, facture mensuelle).",
        example: `{
  "error": {
    "code": "billing_mode_not_supported",
    "message": "Ce mode de facturation n'est pas encore disponible via l'API. Contactez le support Youdégo."
  }
}`,
      },
      {
        status: 400,
        code: 'invalid_sandbox_scenario',
        label: 'Scénario inconnu',
        description: 'La valeur de sandbox.scenario ne fait pas partie des scénarios reconnus.',
        example: `{
  "error": {
    "code": "invalid_sandbox_scenario",
    "message": "Scénario sandbox inconnu. Valides : request_failed, delivery_canceled, delivery_incident."
  }
}`,
      },
      {
        status: 422,
        code: 'external_ref_exists',
        label: 'Référence déjà utilisée',
        description:
          "Une course porte déjà cette externalRef. La réponse indique l'objet existant, ce qui permet de reprendre le suivi sans créer de doublon.",
        example: `{
  "error": {
    "code": "external_ref_exists",
    "message": "Une livraison avec externalRef \\"CMD-2041\\" existe déjà.",
    "deliveryId": "1343228b-…"
  }
}`,
      },
      {
        status: 422,
        code: 'estimate_unavailable',
        label: 'Itinéraire introuvable',
        description: "Aucun trajet routier calculable entre les deux points.",
        example: `{
  "error": { "code": "estimate_unavailable", "message": "Impossible de calculer un itinéraire entre ces deux points." }
}`,
      },
      {
        status: 404,
        code: 'payment_not_found',
        label: 'Paiement introuvable',
        description: "Le payment.paymentId fourni n'existe pas ou ne vous appartient pas.",
        example: `{
  "error": { "code": "payment_not_found", "message": "Paiement introuvable." }
}`,
      },
      ...IDEMPOTENCY_ERRORS,
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'get-request',
    method: 'GET',
    path: '/delivery-requests/:id',
    title: 'Suivre une demande',
    description:
      "État de la recherche de livreur. Dès qu'elle aboutit, deliveryId porte la livraison créée et vous basculez sur les routes /deliveries.",
    pathParams: [
      { name: 'id', type: 'string', required: true, description: 'Identifiant de la demande (réponse 202 de la création EXPRESS).' },
    ],
    responses: [
      {
        status: 200,
        label: 'État de la demande',
        description:
          'status vaut pending, accepted, failed, expired ou canceled. failureReason précise la cause d\'un échec.',
        example: `{
  "object": "delivery_request",
  "id": "66584ffa-…",
  "status": "accepted",
  "mode": "EXPRESS",
  "externalRef": "CMD-2041",
  "deliveryId": "1343228b-…",
  "pricing": { "amount": 700, "currency": "XOF", "distanceKm": 4.42 },
  "failureReason": null,
  "expiresAt": "…",
  "createdAt": "…"
}`,
      },
      NOT_FOUND('Demande de livraison'),
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'cancel-request',
    method: 'POST',
    path: '/delivery-requests/:id/cancel',
    title: 'Annuler une recherche',
    description:
      "Interrompt la recherche de livreur tant qu'aucun n'a accepté. Passé ce stade, c'est la livraison qu'il faut annuler.",
    pathParams: [{ name: 'id', type: 'string', required: true, description: 'Identifiant de la demande.' }],
    bodyFields: [
      { name: 'reason', type: 'string', required: false, description: 'Motif conservé pour le suivi.' },
    ],
    sampleBody: `{
  "reason": "Commande annulée par le client"
}`,
    responses: [
      {
        status: 200,
        label: 'Demande annulée',
        description: 'La recherche est interrompue ; aucun livreur ne sera sollicité.',
        example: `{
  "object": "delivery_request",
  "id": "66584ffa-…",
  "status": "canceled",
  "deliveryId": null
}`,
      },
      {
        status: 409,
        code: 'cancellation_not_allowed',
        label: 'Trop tard',
        description:
          "La demande n'est plus en attente : elle a été acceptée, a échoué ou a expiré. Si un livreur a accepté, annulez la livraison.",
        example: `{
  "error": {
    "code": "cancellation_not_allowed",
    "message": "Cette demande n'est plus en attente (statut « accepted »). Un livreur a accepté : annulez la livraison via POST /v1/deliveries/:id/cancel."
  }
}`,
      },
      NOT_FOUND('Demande de livraison'),
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'get-delivery',
    method: 'GET',
    path: '/deliveries/:id',
    title: 'Consulter une livraison',
    description: "Détail complet : statut, livreur, tarif, horodatages et preuves photo.",
    pathParams: [{ name: 'id', type: 'string', required: true, description: 'Identifiant de la livraison.' }],
    responses: [
      {
        status: 200,
        label: 'Livraison',
        description: 'Le champ status suit les valeurs listées dans GET /v1/config.',
        example: `{
  "object": "delivery",
  "id": "1343228b-…",
  "status": "delivered",
  "mode": "EXPRESS",
  "type": "COLIS",
  "externalRef": "CMD-2041",
  "proof": {
    "pickupPhotoUrl": "https://api.youdego.app/api/uploads/…",
    "deliveryPhotoUrl": null
  },
  "recipient": { "name": "Awa Dossou", "phone": "0197000000" },
  "pickup":  { "latitude": 6.3703, "longitude": 2.3912, "comment": null },
  "dropoff": { "latitude": 6.3654, "longitude": 2.4183, "comment": null },
  "courier": { "firstName": "Kofi", "phone": "0190000000" },
  "pricing": { "amount": 700, "currency": "XOF", "distanceKm": 4.42 },
  "timestamps": {
    "createdAt": "…", "assignedAt": "…", "pickedUpAt": "…", "deliveredAt": "…", "canceledAt": null
  }
}`,
      },
      NOT_FOUND('Livraison'),
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'tracking',
    method: 'GET',
    path: '/deliveries/:id/tracking',
    title: 'Suivre en temps réel',
    description:
      "Position du livreur et jalons de la course, pour alimenter une carte côté client. La position n'est renvoyée que pendant le transport (statuts assigned et picked_up).",
    pathParams: [{ name: 'id', type: 'string', required: true, description: 'Identifiant de la livraison.' }],
    responses: [
      {
        status: 200,
        label: 'Suivi',
        description:
          "courierLocation vaut null tant qu'aucun livreur n'est assigné, après la livraison, ou si sa position n'est pas disponible.",
        example: `{
  "object": "tracking",
  "deliveryId": "1343228b-…",
  "status": "picked_up",
  "externalRef": "CMD-2041",
  "courier": { "firstName": "Kofi", "phone": "0190000000" },
  "courierLocation": { "latitude": 6.3688, "longitude": 2.4051, "updatedAt": "…" },
  "pickup":  { "latitude": 6.3703, "longitude": 2.3912 },
  "dropoff": { "latitude": 6.3654, "longitude": 2.4183 },
  "timestamps": {
    "createdAt": "…", "assignedAt": "…", "arrivedAtPickupAt": "…",
    "pickedUpAt": "…", "deliveredAt": null, "canceledAt": null
  },
  "proof": { "pickupPhotoUrl": "https://api.youdego.app/api/uploads/…", "deliveryPhotoUrl": null }
}`,
      },
      NOT_FOUND('Livraison'),
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'list-deliveries',
    method: 'GET',
    path: '/deliveries',
    title: 'Lister vos livraisons',
    description: 'Liste paginée, filtrable par statut, par période et par votre propre référence.',
    queryParams: [
      { name: 'status', type: 'string', required: false, description: 'Filtre sur le statut.', values: DELIVERY_STATUSES.map((s) => ({ value: s.value.toUpperCase(), description: s.description })) },
      { name: 'externalRef', type: 'string', required: false, description: 'Retrouve la course portant votre référence.' },
      { name: 'from', type: 'string', required: false, description: 'Date de début, format ISO 8601.' },
      { name: 'to', type: 'string', required: false, description: 'Date de fin, format ISO 8601.' },
      { name: 'page', type: 'number', required: false, default: '1', description: 'Numéro de page.' },
      { name: 'limit', type: 'number', required: false, default: '20', description: 'Taille de page, 100 maximum.' },
    ],
    responses: [
      {
        status: 200,
        label: 'Liste',
        description: 'Les livraisons les plus récentes en premier.',
        example: `{
  "object": "list",
  "data": [ { "object": "delivery", "id": "1343228b-…", "status": "delivered", "externalRef": "CMD-2041" } ],
  "pagination": { "total": 1, "page": 1, "limit": 20, "totalPages": 1 }
}`,
      },
      {
        status: 400,
        code: 'invalid_request',
        label: 'Filtre invalide',
        description: 'Statut inconnu ou date mal formée.',
        example: `{
  "error": { "code": "invalid_request", "message": "status invalide. Valides : CREATED, ASSIGNED, PICKED_UP, DELIVERED, CANCELED, INCIDENT." }
}`,
      },
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'cancel-delivery',
    method: 'POST',
    path: '/deliveries/:id/cancel',
    title: 'Annuler une livraison',
    description:
      "Annule une course qu'aucun livreur n'a encore prise en charge. Après assignation, l'annulation implique compensations et remboursements : passez par le support.",
    pathParams: [{ name: 'id', type: 'string', required: true, description: 'Identifiant de la livraison.' }],
    bodyFields: [{ name: 'reason', type: 'string', required: false, description: 'Motif conservé pour le suivi.' }],
    sampleBody: `{
  "reason": "Commande annulée par le client"
}`,
    responses: [
      {
        status: 200,
        label: 'Livraison annulée',
        description: 'Aucun frais n\'est appliqué puisque la course n\'était pas encore assignée.',
        example: `{
  "object": "delivery",
  "id": "1343228b-…",
  "status": "canceled"
}`,
      },
      {
        status: 409,
        code: 'cancellation_not_allowed',
        label: 'Statut non annulable',
        description: 'La course est déjà assignée, en cours, livrée ou annulée.',
        example: `{
  "error": {
    "code": "cancellation_not_allowed",
    "message": "Annulation impossible au statut « assigned ». Seules les livraisons non assignées sont annulables via l'API — contactez le support Youdégo."
  }
}`,
      },
      NOT_FOUND('Livraison'),
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'create-payment',
    method: 'POST',
    path: '/payments',
    title: 'Initier un paiement',
    description:
      "Déclenche une demande Mobile Money sur le téléphone du payeur. Le paiement n'est pas confirmé immédiatement : attendez l'événement payment.succeeded, puis passez son id à la création de la livraison.",
    idempotent: true,
    bodyFields: [
      { name: 'amount', type: 'number', required: true, description: 'Montant en francs CFA, entier, entre 1 et 10 000 000.' },
      { name: 'payerPhone', type: 'string', required: true, description: 'Téléphone du payeur, format local (0XXXXXXXXX).' },
      { name: 'method', type: 'string', required: false, default: 'MTN_MOMO', description: 'Opérateur Mobile Money sollicité.', values: PAYMENT_PROVIDERS },
    ],
    sampleBody: `{
  "amount": 700,
  "payerPhone": "0197000000",
  "method": "MTN_MOMO"
}`,
    responses: [
      {
        status: 201,
        label: 'Paiement initié',
        description:
          "En mode test, aucun appel réel n'est fait : le paiement est réglé automatiquement après quelques secondes (et échoue si le montant finit par 99).",
        example: `{
  "object": "payment",
  "id": "cf9ba5f0-…",
  "status": "pending",
  "amount": 700,
  "currency": "XOF",
  "provider": "FEDAPAY",
  "deliveryId": null,
  "paidAt": null,
  "createdAt": "…",
  "notice": "Le payeur doit confirmer le paiement sur son téléphone (USSD)…"
}`,
      },
      {
        status: 400,
        code: 'invalid_request',
        label: 'Montant ou payeur invalide',
        description: 'Montant hors bornes, opérateur inconnu ou téléphone absent.',
        example: `{
  "error": { "code": "invalid_request", "message": "amount doit être un nombre fini entre 1 et 10 000 000 (XOF)." }
}`,
      },
      {
        status: 502,
        code: 'payment_provider_error',
        label: 'Opérateur injoignable',
        description: "L'opérateur Mobile Money a refusé la demande. Le paiement est marqué en échec, vous pouvez réessayer.",
        example: `{
  "error": { "code": "payment_provider_error", "message": "Échec d'initiation du paiement Mobile Money." }
}`,
      },
      ...IDEMPOTENCY_ERRORS,
      ...AUTH_ERRORS,
    ],
  },

  {
    id: 'get-payment',
    method: 'GET',
    path: '/payments/:id',
    title: 'Consulter un paiement',
    description: "État d'un paiement initié via l'API. Complète les webhooks payment.* en cas de doute.",
    pathParams: [{ name: 'id', type: 'string', required: true, description: 'Identifiant du paiement.' }],
    responses: [
      {
        status: 200,
        label: 'Paiement',
        description: 'status vaut pending, success, failed ou canceled.',
        example: `{
  "object": "payment",
  "id": "cf9ba5f0-…",
  "status": "success",
  "amount": 700,
  "currency": "XOF",
  "provider": "FEDAPAY",
  "deliveryId": null,
  "paidAt": "…",
  "createdAt": "…"
}`,
      },
      NOT_FOUND('Paiement'),
      ...AUTH_ERRORS,
    ],
  },
]

/** Scénarios reconnus par le simulateur sandbox (mode test uniquement). */
export const SANDBOX_SCENARIOS = SANDBOX_SCENARIOS_ENUM.map((s) => ({
  scenario: s.value,
  effect: s.description,
}))
